/**
 * Users — Groups tab (W1a-09)
 *
 * Tenant-scoped user-group CRUD. Groups are named user bundles for
 * permission grants — admin curates membership explicitly (manual). v1
 * supports `membershipType='manual'` only; `'dynamic'` (predicate-based)
 * is scaffolded in the schema but rejected at create time (F-W1a-09.1).
 *
 * W1a-10 (next feature) lands the group detail page (members tab + role
 * grants tab); W1a-09 owns ONLY the list/CRUD of the group itself. The
 * row click navigates to the detail route — that page lands with W1a-10
 * but the route file already exists in the scaffolded UI.
 *
 * Catalog Groups (Wave 5) note: this is the user-groups primitive.
 * Catalog Groups (agent groups, workflow groups, etc., per
 * `docs/catalog-groups-feature.md`) will EXTEND this with new tables —
 * NOT replace `user_groups`. The hook namespace `useUserGroups…` keeps
 * the `useGroups…` namespace open for that future work.
 *
 * The legacy `mock-users-data.ts` `GROUPS` array is no longer imported
 * here (the page uses live data); other consumers may still reference it
 * (Insights filter bar etc.) and they swap to the hook in their own
 * features as F-W1a-09.3.
 */

import { createFileRoute, useNavigate, Outlet, useChildMatches } from '@tanstack/react-router';
import { useState, useEffect, useMemo } from 'react';
import {
  Plus, Search, Users as UsersIcon, UsersRound, Sparkles,
  MoreHorizontal, Eye, Pencil, Trash2, Copy, X as XIcon,
  Loader2, Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useUserGroups,
  useCreateUserGroup,
  useUpdateUserGroup,
  useDeleteUserGroup,
  type UserGroupRow,
} from '@/lib/use-users';
import { useHasPermission } from '@/lib/use-has-permission';

export const Route = createFileRoute('/users/groups')({
  component: GroupsTab,
});

function GroupsTab() {
  // Closes #76 — when /users/groups/$groupId matches, TanStack treats
  // groups.$groupId.tsx as a CHILD of groups.tsx in the route tree (see
  // routeTree.gen.ts → UsersGroupsRouteWithChildren). Without an Outlet
  // here the child route's component never mounts and the list keeps
  // rendering for the detail URL.
  //
  // useChildMatches returns the matched child route(s) for the current
  // location. When non-empty, render <Outlet /> so the detail page
  // takes over; when empty (i.e. exactly /users/groups), render the
  // list. The list state hooks live in a separate component so React's
  // rules-of-hooks aren't violated by an early conditional return.
  const childMatches = useChildMatches();
  if (childMatches.length > 0) {
    return <Outlet />;
  }
  return <GroupsListView />;
}

function GroupsListView() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'manual' | 'dynamic'>('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroupRow | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const { groups, isLoading } = useUserGroups({
    search: search.trim() || undefined,
  });
  const deleteMutation = useDeleteUserGroup();

  const canWrite = useHasPermission('write', 'groups');
  // W1a-09 D1: group delete is `write:groups` (admin+) not `delete:groups`
  // (owner-only — reserved for future destructive-cascade flows).
  const canDelete = canWrite;

  const filtered = useMemo(() => {
    return groups.filter((g) => {
      if (typeFilter !== 'all' && g.membershipType !== typeFilter) return false;
      return true;
    });
  }, [groups, typeFilter]);

  const { sorted, sortConfig, handleSort } = useTableSort(filtered, 'name');
  const hasActiveFilters = typeFilter !== 'all';

  const stats = {
    total: groups.length,
    manual: groups.filter((g) => g.membershipType === 'manual').length,
    dynamic: groups.filter((g) => g.membershipType === 'dynamic').length,
  };

  const openCreate = () => {
    setEditingGroup(null);
    setEditorOpen(true);
  };

  const openEdit = (g: UserGroupRow) => {
    setEditingGroup(g);
    setEditorOpen(true);
  };

  const openDetail = (g: UserGroupRow) => {
    navigate({
      to: '/users/groups/$groupId' as any,
      params: { groupId: g.id } as any,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync({ groupId: id });
      toast.success('Group deleted');
      setConfirmDeleteId(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete group';
      toast.error(msg);
    }
  };

  const groupToDelete =
    confirmDeleteId ? groups.find((g) => g.id === confirmDeleteId) ?? null : null;

  return (
    <div className="flex flex-col h-full p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Groups</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Named user bundles for bulk permission grants. {stats.total} group{stats.total === 1 ? '' : 's'} total.
          </p>
        </div>
        {canWrite && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> New Group
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Total" value={stats.total} color="text-foreground" />
        <StatCard label="Manual" value={stats.manual} color="text-blue-600 dark:text-blue-400" />
        <StatCard label="Dynamic (v1.1)" value={stats.dynamic} color="text-violet-600 dark:text-violet-400" />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">All Types</SelectItem>
              <SelectItem value="manual" className="text-xs">Manual</SelectItem>
              <SelectItem value="dynamic" className="text-xs">Dynamic</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setTypeFilter('all')}>
              <XIcon className="h-3 w-3 mr-1" /> Reset
            </Button>
          )}
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground tabular-nums">
            {sorted.length} of {stats.total}
          </span>
        </CardContent>
      </Card>

      {/* Table */}
      <div className="rounded-lg border bg-card shadow-sm flex-1 overflow-auto">
        <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 'auto', minWidth: 260 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 190 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 96 }} />
          </colgroup>
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr className="border-b">
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <SortableHeader label="Group" sortKey="name" currentSort={sortConfig} onSort={handleSort} />
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <SortableHeader label="Type" sortKey="membershipType" currentSort={sortConfig} onSort={handleSort} />
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <SortableHeader label="Members" sortKey="memberCount" currentSort={sortConfig} onSort={handleSort} />
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Created by
              </th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Last updated
              </th>
              <th className="px-2 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6}>
                  <div className="py-16 text-center">
                    <Loader2 className="h-6 w-6 mx-auto text-muted-foreground/40 animate-spin mb-2" />
                    <p className="text-xs text-muted-foreground">Loading groups…</p>
                  </div>
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="py-16 text-center">
                    <UsersRound className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                    {/*
                      Closes #133. The hook server-filters by search, so
                      `stats.total === 0` no longer reliably means
                      "tenant is empty". Branch on whether ANY filter or
                      search input is active. If yes → filter-empty
                      copy + Clear filters; if no → genuine empty state.
                    */}
                    {(search.trim() !== '' || typeFilter !== 'all') ? (
                      <>
                        <p className="text-sm font-medium">No groups match your search</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Try a different search term or clear the filter.
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-3"
                          onClick={() => {
                            setSearch('');
                            setTypeFilter('all');
                          }}
                        >
                          <XIcon className="h-3 w-3 mr-1.5" /> Clear search & filters
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium">No groups yet</p>
                        {canWrite && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Create your first group to start grouping users.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              sorted.map((g) => (
                <GroupRow
                  key={g.id}
                  group={g}
                  canWrite={canWrite}
                  canDelete={canDelete}
                  onOpen={() => openDetail(g)}
                  onEdit={() => openEdit(g)}
                  onDelete={() => setConfirmDeleteId(g.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <GroupEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        group={editingGroup}
      />

      <AlertDialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => !open && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete group</AlertDialogTitle>
            <AlertDialogDescription>
              Delete{' '}
              <span className="font-semibold">{groupToDelete?.name ?? 'this group'}</span>?
              {' '}
              {groupToDelete && groupToDelete.memberCount > 0 ? (
                <>
                  {groupToDelete.memberCount} member
                  {groupToDelete.memberCount === 1 ? ' will be' : 's will be'} removed
                  from this group. (Users themselves are not deleted.)
                </>
              ) : (
                'This group has no members.'
              )}{' '}
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                confirmDeleteId && handleDelete(confirmDeleteId)
              }
              className="bg-red-600 hover:bg-red-700"
            >
              Delete group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
        <div className={cn('text-xl font-bold tabular-nums mt-0.5', color)}>{value}</div>
      </CardContent>
    </Card>
  );
}

function GroupRow({
  group, canWrite, canDelete, onOpen, onEdit, onDelete,
}: {
  group: UserGroupRow;
  canWrite: boolean;
  canDelete: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const typeStyles: Record<
    'manual' | 'dynamic',
    { icon: any; color: string; label: string }
  > = {
    manual: {
      icon: UsersIcon,
      color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
      label: 'Manual',
    },
    dynamic: {
      icon: Sparkles,
      color: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30',
      label: 'Dynamic',
    },
  };
  const style = typeStyles[group.membershipType];
  const TypeIcon = style.icon;

  const creatorName = group.createdBy.firstName || group.createdBy.lastName
    ? [group.createdBy.firstName, group.createdBy.lastName].filter(Boolean).join(' ')
    : group.createdBy.email ?? '—';

  return (
    <tr className="border-b text-xs hover:bg-muted/30 cursor-pointer" onClick={onOpen}>
      <td className="px-4 py-3 align-middle">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <UsersRound className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium truncate">{group.name}</span>
          </div>
          {group.description && (
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
              {group.description}
            </div>
          )}
        </div>
      </td>

      <td className="px-2 py-3 align-middle">
        <Badge variant="outline" className={cn('text-[10px] gap-1 w-fit', style.color)}>
          <TypeIcon className="h-2.5 w-2.5" />
          {style.label}
        </Badge>
      </td>

      <td className="px-2 py-3 align-middle">
        <span className="tabular-nums font-semibold">{group.memberCount}</span>
      </td>

      <td className="px-2 py-3 align-middle">
        <span className="text-[11px] text-muted-foreground truncate" title={creatorName}>
          {creatorName}
        </span>
      </td>

      <td className="px-2 py-3 align-middle">
        <span className="text-muted-foreground tabular-nums text-[11px]">
          {new Date(group.updatedAt).toLocaleDateString()}
        </span>
      </td>

      <td className="px-2 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-0">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="View details" onClick={onOpen}>
            <Eye className="h-3.5 w-3.5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="More">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem className="text-xs" onSelect={onOpen}>
                <Eye className="h-3.5 w-3.5 mr-2" /> View details
              </DropdownMenuItem>
              {canWrite && (
                <DropdownMenuItem className="text-xs" onSelect={onEdit}>
                  <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className="text-xs" onSelect={() => {
                navigator.clipboard.writeText(group.id);
                toast.success('Group ID copied');
              }}>
                <Copy className="h-3.5 w-3.5 mr-2" /> Copy ID
              </DropdownMenuItem>
              {canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-red-600 dark:text-red-400"
                    onSelect={onDelete}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete group
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

// ─── Group editor dialog ───────────────────────────────────────────────────

function GroupEditorDialog({
  open, onOpenChange, group,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: UserGroupRow | null;
}) {
  const isEditing = group !== null;
  const createMutation = useCreateUserGroup();
  const updateMutation = useUpdateUserGroup();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [membershipType, setMembershipType] = useState<'manual' | 'dynamic'>('manual');
  const [error, setError] = useState<string | null>(null);

  // Reset state when opening or switching modes.
  useEffect(() => {
    if (open) {
      setName(group?.name ?? '');
      setDescription(group?.description ?? '');
      setMembershipType(group?.membershipType ?? 'manual');
      setError(null);
    }
  }, [open, group]);

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    setError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Name is required.');
      return;
    }
    if (trimmedName.length > 200) {
      setError('Name must be 200 characters or fewer.');
      return;
    }

    try {
      if (isEditing && group) {
        await updateMutation.mutateAsync({
          groupId: group.id,
          body: {
            name: trimmedName !== group.name ? trimmedName : undefined,
            description:
              (description || null) !== (group.description ?? null)
                ? description.trim() || null
                : undefined,
          },
        });
        toast.success('Group updated');
      } else {
        await createMutation.mutateAsync({
          name: trimmedName,
          description: description.trim() || null,
          membershipType,
        });
        toast.success('Group created');
      }
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      if (msg.includes('NAME_CONFLICT')) {
        setError('A group with that name already exists.');
      } else if (msg.includes('DYNAMIC_GROUPS_DEFERRED')) {
        setError(
          'Dynamic groups (predicate-based membership) are coming in v1.1.',
        );
      } else if (msg.includes('VALIDATION_ERROR')) {
        setError('Provide a name or description change to save.');
      } else {
        setError(msg);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit group' : 'New group'}</DialogTitle>
          <DialogDescription>
            Named user bundle for bulk permission grants. Members are
            curated manually in v1; predicate-based dynamic groups land
            in v1.1.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="group-name" className="text-xs">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Engineering Leads"
              maxLength={200}
              className="h-8 text-xs"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="group-description" className="text-xs">Description</Label>
            <Textarea
              id="group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this group for?"
              maxLength={1000}
              rows={3}
              className="text-xs"
            />
          </div>

          {!isEditing && (
            <div className="space-y-1.5">
              <Label className="text-xs">Membership type</Label>
              <Select
                value={membershipType}
                onValueChange={(v) => setMembershipType(v as 'manual' | 'dynamic')}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual" className="text-xs">
                    Manual — admin curates members explicitly
                  </SelectItem>
                  <SelectItem value="dynamic" className="text-xs" disabled>
                    Dynamic — predicate-based (coming in v1.1)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {isEditing && (
            <p className="text-[11px] text-muted-foreground">
              Membership type cannot be changed after creation in v1.
            </p>
          )}

          {error && (
            <div className="text-xs text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isLoading || !name.trim()}
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            {isEditing ? 'Save changes' : 'Create group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
